/**
 * AI Chatbot Mock Data for Investor Presentations
 * @feature VITE_INVESTOR_DEMO
 * @task B.INV.CHAT - AI Chatbot Widget
 *
 * Data aligned with actual demo data:
 * - US: demo-data.ts
 * - Brazil: demo-data-br.ts
 */

export interface SuggestedQuery {
  id: string;
  icon: 'chart' | 'invoice' | 'report' | 'trend' | 'alert';
  label: string;
  labelPtBR: string;
  responseKey: string;
}

export interface ChatResponse {
  en: string;
  ptBR: string;
}

export const suggestedQueries: SuggestedQuery[] = [
  {
    id: 'cash-forecast',
    icon: 'chart',
    label: "What's my 30-day cash forecast?",
    labelPtBR: 'Qual é a previsão de caixa para 30 dias?',
    responseKey: 'cash-forecast',
  },
  {
    id: 'overdue-invoices',
    icon: 'invoice',
    label: 'Which invoices are overdue?',
    labelPtBR: 'Quais faturas estão vencidas?',
    responseKey: 'overdue-invoices',
  },
  {
    id: 'overdue-bills',
    icon: 'alert',
    label: 'Which bills are overdue?',
    labelPtBR: 'Quais contas a pagar estão vencidas?',
    responseKey: 'overdue-bills',
  },
  {
    id: 'top-expenses',
    icon: 'report',
    label: 'What are my top expenses?',
    labelPtBR: 'Quais são as minhas maiores despesas?',
    responseKey: 'top-expenses',
  },
  {
    id: 'revenue-trend',
    icon: 'trend',
    label: 'How is revenue trending?',
    labelPtBR: 'Como está a tendência de receita?',
    responseKey: 'revenue-trend',
  },
];

export const mockResponses: Record<string, ChatResponse> = {
  'cash-forecast': {
    en: `Based on your current data, here's your 30-day cash forecast:

**Starting Balance:** $342,890.50
**Expected Inflows:** $156,200.00
**Expected Outflows:** $98,450.00
**Projected Balance:** $400,640.50

Your cash position looks healthy with a **17% increase** expected. The main inflows are from 3 invoices due within 30 days.`,
    ptBR: `Com base nos seus dados atuais, aqui está a previsão de caixa para 30 dias:

**Saldo Inicial:** R$ 185.000,00
**Entradas Previstas:** R$ 198.500,00
**Saídas Previstas:** R$ 86.932,49
**Saldo Projetado:** R$ 296.567,51

Sua posição de caixa está saudável com aumento de **60%** esperado. As principais entradas são de 5 NF-e a vencer em 30 dias.`,
  },
  'overdue-invoices': {
    // US Data: 1 overdue invoice - INV-2026-0002, $48,250, Global Enterprises Inc.
    en: `You have **1 overdue invoice** totaling **$48,250.00**:

1. **INV-2026-0002** - Global Enterprises Inc.
   Amount: $48,250.00 | Due: 10 days ago

Action: I recommend sending a payment reminder today.`,
    // Brazil Data: 1 overdue invoice - NF-e 000001240, R$ 4,000, E-commerce Brasil Digital LTDA
    ptBR: `Você tem **1 fatura vencida** totalizando **R$ 4.000,00**:

1. **NF-e 000001240** - E-commerce Brasil Digital LTDA
   Valor: R$ 4.000,00 | Vencida há: 19 dias

Ação: Recomendo entrar em contato com o cliente para pagamento.`,
  },
  'overdue-bills': {
    // US Data: 2 overdue bills - $8,400 (Atlassian) + $8,920 (HP) = $17,320
    en: `You have **2 overdue bills** totaling **$17,320.00**:

1. **BILL-2026-0005** - Atlassian Corporation
   Amount: $8,400.00 | Due: 5 days ago
   Items: Jira Software Premium, Confluence Premium, Support

2. **BILL-2026-0015** - HP Inc.
   Amount: $8,920.00 | Due: 5 days ago
   Items: HP LaserJet Enterprise (2), HP 27" Monitors (8)

Action: I recommend processing these payments immediately to avoid late fees.`,
    // Brazil Data: 1 overdue bill - CP-2026-0007, R$ 18,500, Advocacia Silva & Associados
    ptBR: `Você tem **1 conta a pagar vencida** totalizando **R$ 18.500,00**:

1. **CP-2026-0007** - Advocacia Silva & Associados
   Valor: R$ 18.500,00 | Vencida há: 3 dias
   Serviço: Assessoria Jurídica - Contrato Comercial
   Pagamento: PIX

Ação: Recomendo processar este pagamento imediatamente para evitar juros e manter bom relacionamento com o fornecedor.`,
  },
  'top-expenses': {
    en: `Your top 5 expense categories this month:

1. **Cloud Infrastructure** - $30,190 (35%)
2. **Salaries & Wages** - $42,000 (32%)
3. **Rent & Utilities** - $12,000 (14%)
4. **Software Subscriptions** - $8,400 (10%)
5. **Marketing** - $13,500 (9%)

Cloud costs increased 12% from last month due to higher compute usage.`,
    ptBR: `Suas 5 principais categorias de despesas este mês:

1. **Mercadorias** - R$ 15.432,50 (18%)
2. **Serviços de TI** - R$ 12.500,00 (14%)
3. **Energia Elétrica** - R$ 8.750,00 (10%)
4. **Assessoria Jurídica** - R$ 18.500,00 (21%)
5. **Aluguel** - R$ 15.000,00 (17%)

Despesas com assessoria jurídica aumentaram devido a novos contratos comerciais.`,
  },
  'revenue-trend': {
    en: `Revenue trend analysis (last 6 months):

| Month | Revenue | Growth |
|-------|---------|--------|
| Aug   | $125K   | -      |
| Sep   | $142K   | +13.6% |
| Oct   | $138K   | -2.8%  |
| Nov   | $165K   | +19.6% |
| Dec   | $189K   | +14.5% |
| Jan   | $210K   | +11.1% |

**Average monthly growth: 11.2%**
You're on track to exceed Q1 targets by 8%.`,
    ptBR: `Análise de tendência de receita (últimos 6 meses):

| Mês | Receita | Crescimento |
|-----|---------|-------------|
| Ago | R$ 165K | -           |
| Set | R$ 178K | +7,9%       |
| Out | R$ 185K | +3,9%       |
| Nov | R$ 192K | +3,8%       |
| Dez | R$ 195K | +1,6%       |
| Jan | R$ 198K | +1,5%       |

**Crescimento médio mensal: 3,7%**
Receita estável com crescimento consistente. Total de R$ 198.500,00 em NF-e emitidas.`,
  },
  default: {
    en: `I can help you with financial insights. Try asking about:
- Cash flow forecasts
- Overdue invoices (what customers owe you)
- Overdue bills (what you owe vendors)
- Expense analysis
- Revenue trends

What would you like to know?`,
    ptBR: `Posso ajudá-lo com insights financeiros. Tente perguntar sobre:
- Previsões de fluxo de caixa
- Faturas vencidas (o que clientes devem a você)
- Contas a pagar vencidas (o que você deve a fornecedores)
- Análise de despesas
- Tendências de receita

O que gostaria de saber?`,
  },
};

/**
 * Find the best matching response key based on user message
 * Distinguishes between:
 * - Invoices (AR - Accounts Receivable) = what customers owe us
 * - Bills (AP - Accounts Payable) = what we owe vendors
 */
export function findResponseKey(message: string): string {
  const lower = message.toLowerCase();

  // Cash forecast keywords
  if (
    lower.includes('cash') ||
    lower.includes('caixa') ||
    lower.includes('forecast') ||
    lower.includes('previsao') ||
    lower.includes('previsão') ||
    lower.includes('balance') ||
    lower.includes('saldo')
  ) {
    return 'cash-forecast';
  }

  // Bills (Accounts Payable) keywords - check BEFORE invoices
  // Bills = contas a pagar = what we owe to vendors/suppliers
  if (
    lower.includes('bill') ||
    lower.includes('contas a pagar') ||
    lower.includes('conta a pagar') ||
    lower.includes('fornecedor') ||
    lower.includes('supplier') ||
    lower.includes('vendor') ||
    lower.includes('pagar') ||
    lower.includes('payable') ||
    lower.includes('devo') ||
    lower.includes('devemos')
  ) {
    return 'overdue-bills';
  }

  // Invoices (Accounts Receivable) keywords
  // Invoices = faturas/NF-e = what customers owe us
  if (
    lower.includes('invoice') ||
    lower.includes('fatura') ||
    lower.includes('nf-e') ||
    lower.includes('nota fiscal') ||
    lower.includes('receivable') ||
    lower.includes('receber') ||
    lower.includes('cliente') ||
    lower.includes('customer') ||
    lower.includes('overdue') ||
    lower.includes('vencid') ||
    lower.includes('atrasad') ||
    lower.includes('late') ||
    lower.includes('unpaid')
  ) {
    return 'overdue-invoices';
  }

  // Expense keywords
  if (
    lower.includes('expense') ||
    lower.includes('despesa') ||
    lower.includes('gasto') ||
    lower.includes('cost') ||
    lower.includes('custo')
  ) {
    return 'top-expenses';
  }

  // Revenue keywords
  if (
    lower.includes('revenue') ||
    lower.includes('receita') ||
    lower.includes('trend') ||
    lower.includes('tendencia') ||
    lower.includes('tendência') ||
    lower.includes('growth') ||
    lower.includes('crescimento')
  ) {
    return 'revenue-trend';
  }

  return 'default';
}
